import { z } from "zod";
import { ticketAndBadgesUserTypes } from "@ac-expo/api-types";
import { DiscountType, TicketUserType } from "@prisma/client";
import { applyDiscount, isNullish } from "@ac-expo/utils";
import {
  addDuplicatedDiscountCodeIssues,
  createDegressivePriceSchema,
  createDiscountCodeSchema,
} from "../discount-code";
import { createPriceSchema } from "../tickets/create-ticket-type.schema";
import { CustomAppearanceSchema, CustomPdfAppearanceSchema } from "../utils";
import { fileSchema } from "../files";

export const DUPLICATED_DISCOUNT_CODE_IN_BADGE_MESSAGE =
  "Ce nom est déjà utilisé par un autre code promo de ce badge";

export const createBadgeTypeSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, "Le nom du badge est requis"),
    quantity: z.coerce.number().int().or(z.literal("Infini")),
    validityDates: z
      .array(
        z
          .date({
            required_error: "La date de validité est requise",
          })
          .refine((date) => !isNaN(date.getTime()), "Date de validité invalide")
      )
      .min(1, "Les dates de validité sont requises"),
    numberEntriesPerDay: z.coerce
      .number()
      .int()
      .gt(0, "Le nombre d'entrées par jour doit être supérieur à 0")
      .or(z.literal("Infini"))
      .nullish(),
    isInfiniteEntries: z.boolean(),
    userType: z.enum(
      ticketAndBadgesUserTypes as [TicketUserType, ...TicketUserType[]]
    ),
    isVisibleOnVisitorTicketing: z.boolean(),
    isAvailableOnOnlineTicketing: z.boolean(),

    isPaid: z.boolean(),
    price: createPriceSchema.nullish(),

    groupOnly: z.boolean(),
    groupMinSize: z.coerce
      .number()
      .int()
      .gt(1, "Les prix de groupes sont à partir de 2")
      .nullish(),

    isDegressivePrice: z.boolean(),
    degressivePrices: z.array(createDegressivePriceSchema).refine(
      (dp) =>
        !dp.some((a, i) =>
          dp.some((b, j) => {
            if (i === j) return false;

            const aMin = !isNullish(a.minQuantity) ? a.minQuantity : 0;
            const aMax = !isNullish(a.maxQuantity) ? a.maxQuantity : Infinity;
            const bMin = !isNullish(b.minQuantity) ? b.minQuantity : 0;
            const bMax = !isNullish(b.maxQuantity) ? b.maxQuantity : Infinity;

            return aMax >= bMin && aMin <= bMax;
          })
        ),
      {
        message: "Les plages de quantités ne doivent pas se chevaucher",
      }
    ),

    areDiscountsCodesEnabled: z.boolean(),
    discountCodes: z.array(createDiscountCodeSchema),
    hasCustomAppearance: z.boolean().default(false),
    customAppearance: CustomAppearanceSchema.nullable(),
    customPdfAppearance: CustomPdfAppearanceSchema.nullable(),
    templatePdf: fileSchema.nullable(),
    hasEventGates: z.boolean().default(false),
    eventGates: z.array(z.string()).default([]),
  })
  .refine(
    ({ groupOnly, groupMinSize }) => !groupOnly || !isNullish(groupMinSize),
    {
      message: "La taille minimale du groupe est requise",
      path: ["groupMinSize"],
    }
  )
  .refine(
    ({ isDegressivePrice, degressivePrices }) =>
      !isDegressivePrice || degressivePrices.length > 0,
    {
      message: "Au moins un prix dégressif est requis",
      path: ["degressivePrices"],
    }
  )
  .refine(
    ({ areDiscountsCodesEnabled, discountCodes }) =>
      !areDiscountsCodesEnabled || discountCodes.length > 0,
    {
      message: "Au moins un code de réduction est requis",
      path: ["discountCodes"],
    }
  )
  .superRefine(
    (
      { isPaid, groupOnly, isDegressivePrice, areDiscountsCodesEnabled },
      ctx
    ) => {
      if (isPaid) return;
      if (groupOnly)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Le prix des badges est nécessaires pour définir des tarif du groupe.",
          path: ["groupOnly"],
        });
      if (isDegressivePrice)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Le prix des badges est nécessaire pour définir des tarifs dégressifs",
          path: ["isDegressivePrice"],
        });
      if (areDiscountsCodesEnabled) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Le prix des badges est nécessaire pour activer les codes de réduction",
          path: ["areDiscountsCodesEnabled"],
        });
      }
    }
  )
  .superRefine(({ discountCodes }, ctx) =>
    addDuplicatedDiscountCodeIssues(discountCodes, ctx, DUPLICATED_DISCOUNT_CODE_IN_BADGE_MESSAGE)
  )
  .superRefine(({ isPaid, price, degressivePrices, discountCodes }, ctx) => {
    if (!isPaid || !price) return;
    degressivePrices?.forEach((dp, i) => {
      if (
        dp.discount.type === DiscountType.euro &&
        dp.discount.value > price.priceTTC
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La réduction ne peut pas être supérieure au prix du badge",
          path: ["degressivePrices", i, "discount", "value"],
        });
      }
    });
    discountCodes?.forEach((dc, i) => {
      if (
        dc.discount.type === DiscountType.euro &&
        dc.discount.value > price.priceTTC
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La réduction ne peut pas être supérieure au prix du badge",
          path: ["discountCodes", i, "discount", "value"],
        });
      }
    });
  })
  .superRefine(({ degressivePrices, price, isPaid }, ctx) => {
    if (!isPaid || !price) return;
    const compareByApplyingDiscount = (
      a: (typeof degressivePrices)[number],
      b: (typeof degressivePrices)[number]
    ) => {
      return (
        applyDiscount(price?.priceTTC, a.discount) -
        applyDiscount(price?.priceTTC, b.discount)
      );
    };
    const sortedDegressivePrices = degressivePrices
      .map((v, i) => ({ ...v, index: i }))
      .sort(compareByApplyingDiscount);
    sortedDegressivePrices.forEach((dp, i) => {
      for (let j = i + 1; j < sortedDegressivePrices.length; j++) {
        const otherDp = sortedDegressivePrices[j];
        if (
          !otherDp.maxQuantity ||
          (dp.minQuantity ?? 0) < otherDp.maxQuantity
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
              "Cette réduction est plus avantageuse qu'une autre pouvant s'appliquer à plus de billets",
            path: ["degressivePrices", dp.index, "discount", "value"],
          });
        }
      }
    });
  });

export type CreateBadgeTypeForm = z.infer<typeof createBadgeTypeSchema>;

export const defaultCreateBadgeTypeValues: CreateBadgeTypeForm = {
  name: "",
  quantity: "Infini" as const,
  validityDates: [],
  numberEntriesPerDay: 1,
  isInfiniteEntries: false,
  userType: TicketUserType.all,
  isVisibleOnVisitorTicketing: false,
  isAvailableOnOnlineTicketing: true,
  isPaid: false,
  price: null,
  groupOnly: false,
  groupMinSize: null,
  isDegressivePrice: false,
  degressivePrices: [],
  areDiscountsCodesEnabled: false,
  discountCodes: [],
  hasCustomAppearance: false,
  customAppearance: null,
  customPdfAppearance: null,
  templatePdf: null,
  hasEventGates: false,
  eventGates: [],
};
